import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Badge, Button, EmptyState, Input, Modal } from '../../../ui';
import { toast } from '../../../ui/toast';
import { useCategories, useCompanies } from '../../../hooks/useFinancialOps';
import { useCreateRule, useRules, useSetRuleStatus } from '../hooks/useRules';
import type { NewRuleInput, RuleDTO } from '../services/rules.service';

const ORIGEM: Record<RuleDTO['origin'], string> = {
  HISTORY: 'Histórico',
  APPROVAL: 'Aprovação',
  MANUAL: 'Manual',
};

function formatarDocumento(doc: string, tipo: string | null): string {
  if (tipo === 'CPF' || doc.length === 11) {
    return `CPF ${doc.slice(0, 3)}.${doc.slice(3, 6)}.${doc.slice(6, 9)}-${doc.slice(9)}`;
  }
  return `CNPJ ${doc.slice(0, 2)}.${doc.slice(2, 5)}.${doc.slice(5, 8)}/${doc.slice(8, 12)}-${doc.slice(12)}`;
}

function resultado(r: RuleDTO): string {
  const partes = [r.categoryName];
  if (r.beneficiaryCompanyName) {
    partes.push(`rateio para ${r.beneficiaryCompanyName}`);
  }
  if (r.excludedFromDre) {
    partes.push('fora da DRE');
  }
  if (r.excludedFromCashFlow) {
    partes.push('fora do caixa');
  }
  if (r.isCardSettlement) {
    partes.push('pagamento de fatura');
  }
  return partes.join(' · ');
}

function mensagemDeErro(err: unknown, padrao: string): string {
  const msg = (err as { response?: { data?: { message?: string } } } | null)?.response?.data
    ?.message;
  return msg || padrao;
}

interface Form {
  type: 'INCOME' | 'EXPENSE';
  por: 'contraparte' | 'palavras';
  documento: string;
  palavras: string;
  categoryId: string;
  beneficiaryCompanyId: string;
  excludedFromDre: boolean;
  excludedFromCashFlow: boolean;
  isCardSettlement: boolean;
}

const VAZIO: Form = {
  type: 'EXPENSE',
  por: 'contraparte',
  documento: '',
  palavras: '',
  categoryId: '',
  beneficiaryCompanyId: '',
  excludedFromDre: false,
  excludedFromCashFlow: false,
  isCardSettlement: false,
};

const SELECT = 'h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink';
const ROTULO = 'block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5';

export function RegrasPage() {
  const { data: companiesData } = useCompanies();
  const { data: categoriesData } = useCategories();
  const [companyId, setCompanyId] = useState('');
  const rules = useRules(companyId);
  const createRule = useCreateRule();
  const setStatus = useSetRuleStatus();
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState<Form>(VAZIO);

  const empresas = (companiesData?.data ?? []) as Array<{
    id: string;
    name: string;
    active?: boolean;
  }>;
  const categorias = (categoriesData?.data ?? []) as Array<{
    id: string;
    name: string;
    type: string;
    active?: boolean;
  }>;
  const lista = rules.data ?? [];

  async function alternarStatus(r: RuleDTO): Promise<void> {
    try {
      if (r.status === 'ACTIVE') {
        const motivo = window.prompt('Motivo da desativação (opcional)');
        if (motivo === null) {
          return;
        }
        await setStatus.mutateAsync({
          id: r.id,
          status: 'DISABLED',
          reason: motivo.trim() || null,
        });
        toast.success('Regra desativada');
      } else {
        await setStatus.mutateAsync({ id: r.id, status: 'ACTIVE' });
        toast.success('Regra reativada');
      }
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível mudar a regra'));
    }
  }

  async function salvar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const rateio = form.type === 'EXPENSE' && form.beneficiaryCompanyId !== '';
    const input: NewRuleInput = {
      companyId,
      type: form.type,
      categoryId: form.categoryId,
      counterpartDocument: form.por === 'contraparte' ? form.documento.trim() || null : null,
      descriptionTokens:
        form.por === 'palavras' ? form.palavras.split(/\s+/).filter((t) => t !== '') : null,
      beneficiaryCompanyId: rateio ? form.beneficiaryCompanyId : null,
      excludedFromDre: !rateio && form.excludedFromDre,
      excludedFromCashFlow: !rateio && form.excludedFromCashFlow,
      isCardSettlement: !rateio && form.isCardSettlement,
    };
    try {
      await createRule.mutateAsync(input);
      toast.success('Regra criada');
      setAberto(false);
      setForm(VAZIO);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível criar a regra'));
    }
  }

  const rateio = form.type === 'EXPENSE' && form.beneficiaryCompanyId !== '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="regras-empresa" className={ROTULO}>
            Empresa
          </label>
          <select
            id="regras-empresa"
            className={SELECT}
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            <option value="">Selecione uma empresa...</option>
            {empresas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {companyId && (
          <Button size="sm" onClick={() => setAberto(true)}>
            <Plus size={16} className="mr-1" /> Nova regra
          </Button>
        )}
      </div>

      <p className="text-xs text-ink-tertiary">
        Uma linha do Open Finance que bate com uma regra ativa entra sozinha, até R$ 5.000.
        Contraparte vence palavras; entre palavras, vence a regra com mais palavras.
      </p>

      {!companyId ? (
        <EmptyState title="Selecione uma empresa" body="As regras são por empresa." />
      ) : lista.length === 0 ? (
        <EmptyState
          title="Nenhuma regra"
          body="A Nami propõe as primeiras a partir do histórico."
        />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-ink-tertiary">
              <th className="px-2 py-2 font-medium">Condição</th>
              <th className="px-2 py-2 font-medium">Tipo</th>
              <th className="px-2 py-2 font-medium">Resultado</th>
              <th className="px-2 py-2 font-medium">Origem</th>
              <th className="px-2 py-2 font-medium">Usos</th>
              <th className="px-2 py-2 font-medium">Contestações</th>
              <th className="px-2 py-2 font-medium">Situação</th>
              <th className="px-2 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {lista.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-2 py-2">
                  {r.counterpartDocument ? (
                    formatarDocumento(r.counterpartDocument, r.counterpartDocumentType)
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {r.descriptionTokens.map((t) => (
                        <Badge key={t} tone="neutral">
                          {t}
                        </Badge>
                      ))}
                    </span>
                  )}
                </td>
                <td className="px-2 py-2">{r.type === 'INCOME' ? 'Receita' : 'Despesa'}</td>
                <td className="px-2 py-2">{resultado(r)}</td>
                <td className="px-2 py-2">{ORIGEM[r.origin]}</td>
                <td className="px-2 py-2">{r.hits}</td>
                <td className="px-2 py-2">
                  {r.contestations > 0 ? (
                    <Badge tone="warning">{r.contestations}</Badge>
                  ) : (
                    r.contestations
                  )}
                </td>
                <td className="px-2 py-2" title={r.disabledReason ?? undefined}>
                  {r.status === 'ACTIVE' ? 'Ativa' : 'Desativada'}
                </td>
                <td className="px-2 py-2 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void alternarStatus(r)}
                    disabled={setStatus.isPending}
                  >
                    {r.status === 'ACTIVE' ? 'Desativar' : 'Reativar'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {aberto && (
        <Modal
          open
          onClose={() => setAberto(false)}
          title="Nova regra"
          description="A regra vale para linhas do Open Finance desta empresa."
          size="sm"
        >
          <form onSubmit={(e) => void salvar(e)} className="space-y-3">
            <div>
              <label htmlFor="regra-tipo" className={ROTULO}>
                Tipo
              </label>
              <select
                id="regra-tipo"
                className={SELECT}
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value as Form['type'], categoryId: '' }))
                }
              >
                <option value="EXPENSE">Despesa</option>
                <option value="INCOME">Receita</option>
              </select>
            </div>
            <fieldset className="flex gap-4 text-sm">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="regra-por"
                  checked={form.por === 'contraparte'}
                  onChange={() => setForm((f) => ({ ...f, por: 'contraparte' }))}
                />
                Por contraparte
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="regra-por"
                  checked={form.por === 'palavras'}
                  onChange={() => setForm((f) => ({ ...f, por: 'palavras' }))}
                />
                Por palavras da descrição
              </label>
            </fieldset>
            {form.por === 'contraparte' ? (
              <Input
                label="CNPJ ou CPF"
                value={form.documento}
                onChange={(e) => setForm((f) => ({ ...f, documento: e.target.value }))}
              />
            ) : (
              <Input
                label="Palavras"
                value={form.palavras}
                onChange={(e) => setForm((f) => ({ ...f, palavras: e.target.value }))}
              />
            )}
            <div>
              <label htmlFor="regra-categoria" className={ROTULO}>
                Categoria
              </label>
              <select
                id="regra-categoria"
                className={SELECT}
                value={form.categoryId}
                onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
              >
                <option value="">Selecione...</option>
                {categorias
                  .filter((c) => c.type === form.type && c.active !== false)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
            {form.type === 'EXPENSE' && (
              <div>
                <label htmlFor="regra-rateio" className={ROTULO}>
                  Rateio para
                </label>
                <select
                  id="regra-rateio"
                  className={SELECT}
                  value={form.beneficiaryCompanyId}
                  onChange={(e) => setForm((f) => ({ ...f, beneficiaryCompanyId: e.target.value }))}
                >
                  <option value="">Esta empresa</option>
                  {empresas
                    .filter((c) => c.id !== companyId && c.active !== false)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </div>
            )}
            {!rateio && (
              <div className="flex flex-col gap-1 text-sm">
                {(
                  [
                    ['excludedFromDre', 'Fora da DRE'],
                    ['excludedFromCashFlow', 'Fora do caixa'],
                    ['isCardSettlement', 'Pagamento de fatura'],
                  ] as const
                ).map(([campo, rotulo]) => (
                  <label key={campo} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form[campo]}
                      onChange={(e) => setForm((f) => ({ ...f, [campo]: e.target.checked }))}
                    />
                    {rotulo}
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setAberto(false)}>
                Cancelar
              </Button>
              <Button type="submit" loading={createRule.isPending}>
                Salvar
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
